import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  Button,
  TextField,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Paper,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Stack,
  CircularProgress
} from '@mui/material';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PaymentIcon from '@mui/icons-material/Payment';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CreditCardIcon from '@mui/icons-material/CreditCard';

// Import Firebase services
import { DashboardService, AppointmentManagementService, TestsManagementService } from '../services/firebase';
import RazorpayService from '../services/RazorpayService';
import { RazorpayResponse, RazorpayError } from '../types/razorpay';
import { doc, updateDoc, Timestamp, addDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { getAuth } from 'firebase/auth';

// Form interfaces
interface NewAppointmentForm {
  patientName: string;
  phone: string;
  age: string;
  gender: string;
  doctorId: string;
  department: string;
  service: string;
  customService?: string;
  appointmentType: string;
  date: Date | null;
  time: Date | null;
  symptoms: string;
  feeAmount: string;
  paymentMode: string;
}

interface FormErrors {
  [key: string]: string;
}

// Firebase services data will be loaded dynamically
let firebaseServices: any = {};

// Invoice generation function for appointment payments
const generateInvoiceForPayment = async (paymentId: string, paymentData: any, db: any) => {
  try {
    console.log('📄 Generating invoice for appointment payment:', paymentId);
    
    const invoiceNumber = `INV-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const currentDate = new Date();
    
    const invoice = {
      invoiceNumber,
      invoiceType: 'payment_receipt',
      
      // Patient details
      patientId: paymentData.patientId,
      patientName: paymentData.patientName,
      
      // Link to payment (1:1 relationship)
      paymentId: paymentId,
      
      // Invoice details
      issueDate: currentDate.toISOString().split('T')[0],
      issueTime: currentDate.toLocaleTimeString(),
      
      // Amount = THIS payment only (NOT cumulative)
      amount: paymentData.amount,
      totalAmount: paymentData.amount,
      paymentMethod: paymentData.paymentMethod,
      transactionId: paymentData.transactionId || paymentData.razorpayPaymentId || `CASH-${Date.now()}`,
      
      // Line items from payment
      items: (paymentData.items && paymentData.items.length > 0) ? paymentData.items.map((item: any) => ({
        name: item.name,
        description: item.description || '',
        quantity: item.quantity || 1,
        rate: item.rate || item.amount,
        amount: item.amount
      })) : [{
        name: paymentData.serviceName || 'Appointment Fee',
        description: paymentData.description || 'Consultation',
        quantity: 1,
        rate: paymentData.amount,
        amount: paymentData.amount
      }],
      
      // Summary
      subtotal: paymentData.amount,
      tax: 0,
      discount: 0,
      
      // Additional info
      description: paymentData.description || `Appointment Payment - ${paymentData.serviceName || 'Consultation'}`,
      notes: `Invoice generated on ${currentDate.toLocaleDateString('en-IN')} at ${currentDate.toLocaleTimeString()}`,
      terms: 'Thank you for your payment',
      
      // Status
      status: 'paid',
      
      // Service type
      serviceType: 'appointment',
      
      // Razorpay details (if applicable)
      razorpayPaymentId: paymentData.razorpayPaymentId || null,
      razorpayOrderId: paymentData.razorpayOrderId || null,
      razorpaySignature: paymentData.razorpaySignature || null,
      
      // Metadata
      isAutoGenerated: true,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    // Save invoice to Firebase
    const invoiceDoc = await addDoc(collection(db, 'invoices'), invoice);
    
    console.log('✅ Invoice generated:', invoiceNumber, 'for appointment payment:', paymentId);
    
    return { success: true, invoiceId: invoiceDoc.id, invoice };
  } catch (error) {
    console.error('❌ Error generating invoice for appointment payment:', error);
    throw error;
  }
};

// Default services fallback
const defaultServices = {
  'Cardiology': ['ECG', 'Echo Cardiogram', 'Stress Test', 'Consultation'],
  'Dermatology': ['Skin Treatment', 'Mole Removal', 'Acne Treatment', 'Consultation'],
  'Orthopedics': ['X-Ray', 'MRI', 'Physiotherapy', 'Consultation'],
  'Neurology': ['EEG', 'Brain MRI', 'Nerve Conduction', 'Consultation'],
  'Pediatrics': ['Vaccination', 'Growth Monitoring', 'Development Check', 'Consultation'],
  'General Medicine': ['Health Checkup', 'Blood Test', 'Consultation'],
  'Gynecology': ['Ultrasound', 'Pap Smear', 'Prenatal Care', 'Consultation'],
  'ENT': ['Hearing Test', 'Throat Examination', 'Sinus Treatment', 'Consultation']
};

// Payment modes - matching mobile app
const paymentModes = ['Pay at Hospital', 'Razorpay', 'Cash'];

// Form interfaces
interface NewAppointmentForm {
  patientName: string;
  phone: string;
  age: string;
  gender: string;
  doctorId: string;
  department: string;
  service: string;
  appointmentType: string;
  date: Date | null;
  time: Date | null;
  symptoms: string;
  feeAmount: string;
  paymentMode: string;
}

// Helper function to normalize appointment status
const normalizeStatus = (status: string): string => {
  const statusMap: { [key: string]: string } = {
    'scheduled': 'Pending',
    'confirmed': 'Confirmed',
    'cancelled': 'Cancelled',
    'completed': 'Completed',
    'pending': 'Pending'
  };
  return statusMap[status?.toLowerCase()] || 'Pending';
};

const Appointments: React.FC = () => {
  const navigate = useNavigate();
  
  // State management
  const [appointments, setAppointments] = useState<any[]>([]);
  const [filteredAppointments, setFilteredAppointments] = useState<any[]>([]);
  const [doctors, setDoctors] = useState<any[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterDate, setFilterDate] = useState('All');
  const [selectedAppointment, setSelectedAppointment] = useState<any>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [actionMenuAppointment, setActionMenuAppointment] = useState<any>(null);
  const [submittingForm, setSubmittingForm] = useState(false);
  
  // Payment collection states
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedPaymentAppointment, setSelectedPaymentAppointment] = useState<any>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showCashConfirmDialog, setShowCashConfirmDialog] = useState(false);
  
  // Status menu states
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [statusMenuAppointment, setStatusMenuAppointment] = useState<any>(null);
  const [showStatusConfirmDialog, setShowStatusConfirmDialog] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<string>('');
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });

  // Form state
  const [newAppointmentForm, setNewAppointmentForm] = useState<NewAppointmentForm>({
    patientName: '',
    phone: '',
    age: '',
    gender: '',
    doctorId: '',
    department: '',
    service: '',
    appointmentType: '',
    date: null,
    time: null,
    symptoms: '',
    feeAmount: '',
    paymentMode: ''
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});

  // Clear any old localStorage values and reset filters on mount
  useEffect(() => {
    localStorage.removeItem('appointmentsFilterStatus');
    localStorage.removeItem('appointmentsFilterDate');
  }, []);

  // Load Firebase data on component mount with real-time updates
  useEffect(() => {
    console.log('🚀 Setting up real-time appointments listener...');
    
    // Set up real-time listener for appointments
    const unsubscribe = AppointmentManagementService.listenToAppointments((appointmentsData) => {
      console.log('📋 Real-time appointments update received:', {
        count: appointmentsData.length,
        data: appointmentsData
      });
      
      if (appointmentsData && appointmentsData.length > 0) {
        // Transform and normalize the data (matching mobile app structure)
        const transformedAppointments = appointmentsData.map((appointment: any) => {
          // Helper function to safely convert dates
          const convertDate = (dateValue: any): string | null => {
            try {
              if (!dateValue) return null;
              
              // If it's already a string, return as is
              if (typeof dateValue === 'string') {
                return dateValue;
              }
              
              // If it's a Firestore timestamp object
              if (dateValue && typeof dateValue === 'object' && dateValue.seconds) {
                return new Date(dateValue.seconds * 1000).toISOString().split('T')[0];
              }
              
              // If it has toDate method (Firestore timestamp)
              if (dateValue && typeof dateValue.toDate === 'function') {
                return dateValue.toDate().toISOString().split('T')[0];
              }
              
              // Try to create Date object
              const date = new Date(dateValue);
              if (!isNaN(date.getTime())) {
                return date.toISOString().split('T')[0];
              }
              
              return null;
            } catch (error) {
              console.warn('Error converting date:', error, 'dateValue:', dateValue);
              return null;
            }
          };

          const transformed = {
            // Use Firebase document ID (firebaseDocId), NOT the internal 'id' field
            id: appointment.firebaseDocId || appointment.id,
            appointmentId: appointment.appointmentId || appointment.id,
            appointmentNumber: appointment.appointmentNumber || appointment.tokenNumber || `KBR-${String(appointment.id).slice(-3)}`,
            tokenNumber: appointment.tokenNumber || null,
            patientName: appointment.patientName || appointment.name || 'Unknown Patient',
            patientAge: appointment.patientAge || appointment.age || 'N/A',
            patientGender: appointment.patientGender || appointment.gender || 'N/A',
            patientPhone: appointment.contactNumber || appointment.patientPhone || appointment.phone || 'N/A',
            phone: appointment.contactNumber || appointment.patientPhone || appointment.phone || 'N/A',
            patientAddress: appointment.patientAddress || appointment.address || 'N/A',
            emergencyContact: appointment.emergencyContact || appointment.contactNumber || 'N/A',
            doctorName: appointment.doctorName || appointment.doctor || 'Unknown Doctor',
            department: appointment.department || appointment.specialty || 'General',
            service: appointment.service || appointment.serviceName || appointment.serviceType || 'Consultation',
            appointmentType: appointment.type || appointment.appointmentType || 'consultation',
            date: appointment.date || appointment.appointmentDate || new Date().toISOString().split('T')[0],
            time: appointment.time || appointment.appointmentTime || appointment.timeSlot || '10:00 AM',
            status: normalizeStatus(appointment.status || 'scheduled'),
            symptoms: appointment.symptoms || appointment.reason || '',
            feeAmount: appointment.fee || appointment.fees || appointment.amount || null,
            paymentMode: appointment.paymentMode || appointment.paymentMethod || 'Cash',
            paymentMethod: appointment.paymentMethod || appointment.paymentMode || 'Cash',
            paymentStatus: appointment.paymentStatus || 'Pending',
            transactionId: appointment.transactionId || null,
            razorpayOrderId: appointment.razorpayOrderId || null,
            age: appointment.age || appointment.patientAge || 'N/A',
            gender: appointment.gender || appointment.patientGender || 'N/A',
            isNewPatient: appointment.isNewPatient || false,
            bookingDate: convertDate(appointment.bookingDate || appointment.createdAt)
          };
          
          console.log('📄 Transformed appointment:', {
            firebaseDocId: appointment.firebaseDocId,
            internalId: appointment.id,
            usingId: transformed.id,
            patientName: transformed.patientName,
            appointmentNumber: transformed.appointmentNumber
          });
          return transformed;
        });
        
        // Only filter out completely invalid IDs (very short or undefined)
        const validAppointments = transformedAppointments.filter((apt: any) => {
          const isValid = apt.id && apt.id.length > 5;
          if (!isValid) {
            console.log('⚠️ Filtered out invalid appointment:', apt);
          }
          return isValid;
        });
        
        console.log(`✅ Real-time update: ${validAppointments.length} valid appointments (filtered ${transformedAppointments.length - validAppointments.length} invalid)`);
        setAppointments(validAppointments);
      } else {
        console.log('⚠️ No appointments in real-time update');
        setAppointments([]);
      }
      
      setLoadingData(false);
    });
    
    // Load doctors separately (one-time load)
    loadDoctors();
    
    // Cleanup listener on unmount
    return () => {
      console.log('🔌 Disconnecting real-time appointments listener');
      unsubscribe();
    };
  }, []);

  // Separate function to load doctors
  const loadDoctors = async () => {
    try {
      const doctorsResult = await DashboardService.getDoctors();
      if (doctorsResult && doctorsResult.length > 0) {
        const doctorsList = doctorsResult.map((doctor: any) => ({
          id: doctor.id,
          name: doctor.name || `Dr. ${doctor.firstName || ''} ${doctor.lastName || ''}`.trim(),
          specialization: doctor.specialization || doctor.specialty || 'General',
          department: doctor.department || doctor.specialization || 'General Medicine'
        }));
        setDoctors(doctorsList);
        console.log(`✅ Loaded ${doctorsList.length} doctors from Firebase`);
      }
    } catch (error) {
      console.error('❌ Error loading doctors:', error);
    }
  };

  // Function to manually refresh appointments (keeping for refresh button)
  const loadAppointmentsData = async () => {
    try {
      setLoadingData(true);
      console.log('🔄 Manually refreshing appointments...');
      
      // Reset all filters to show all data
      setFilterStatus('All');
      setFilterDate('All');
      setSearchQuery('');
      
      // The real-time listener will automatically update the data
      // This is just to show loading state
      setTimeout(() => setLoadingData(false), 500);
    } catch (error) {
      console.error('❌ Error refreshing:', error);
      setLoadingData(false);
    }
  };

  // Helper function to check if date matches filter (same as mobile)
  const checkDateFilter = (appointmentDate: string, filter: string): boolean => {
    const today = new Date();
    const appointmentDateTime = new Date(appointmentDate);
    
    switch (filter) {
      case 'Today':
        return appointmentDateTime.toDateString() === today.toDateString();
      case 'Tomorrow':
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        return appointmentDateTime.toDateString() === tomorrow.toDateString();
      case 'This Week':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return appointmentDateTime >= startOfWeek && appointmentDateTime <= endOfWeek;
      case 'This Month':
        return appointmentDateTime.getMonth() === today.getMonth() && 
               appointmentDateTime.getFullYear() === today.getFullYear();
      case 'All':
        return true;
      default:
        return true;
    }
  };

  // Calculate appointment statistics (matching mobile app)
  const appointmentStats = useMemo(() => {
    if (!appointments || !Array.isArray(appointments)) {
      return {
        total: 0,
        today: 0,
        confirmed: 0,
        admitted: 0,
        pending: 0,
        completed: 0,
        cancelled: 0
      };
    }

    return {
      total: appointments.length,
      today: appointments.filter(apt => checkDateFilter(apt.date, 'Today')).length,
      confirmed: appointments.filter(apt => apt.status === 'Confirmed').length,
      admitted: appointments.filter(apt => apt.status === 'Admitted').length,
      pending: appointments.filter(apt => apt.status === 'Pending').length,
      completed: appointments.filter(apt => apt.status === 'Completed').length,
      cancelled: appointments.filter(apt => apt.status === 'Cancelled').length
    };
  }, [appointments]);

  // Filter appointments based on search, status, and date
  useEffect(() => {
    let filtered = [...appointments];

    // Filter by search query (supports Token Number, Name, Phone Number)
    if (searchQuery && searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase().trim();
      const searchNormalized = searchQuery.replace(/[-\s]/g, '').toLowerCase();
      
      // Filter and score each appointment based on relevance
      const scoredResults = filtered.map(appointment => {
        // Safely get values with fallbacks
        const appointmentNum = (appointment.appointmentNumber || '').toLowerCase();
        const tokenNum = (appointment.tokenNumber || '').toLowerCase();
        const appointmentNumNormalized = appointmentNum.replace(/[-\s]/g, '');
        const tokenNumNormalized = tokenNum.replace(/[-\s]/g, '');
        const patientName = (appointment.patientName || '').toLowerCase();
        const phone = (appointment.phone || '').toString();
        const doctorName = (appointment.doctorName || '').toLowerCase();
        const department = (appointment.department || '').toLowerCase();
        
        let score = 0;
        let matched = false;
        
        // Debug logging
        console.log('Comparing:', {
          search: searchNormalized,
          appointmentNum: appointmentNumNormalized,
          tokenNum: tokenNumNormalized,
          exactMatch: appointmentNumNormalized === searchNormalized || tokenNumNormalized === searchNormalized
        });
        
        // EXACT token match - normalized (highest priority - score 1000)
        // "KBR 001" (becomes "kbr001") should ONLY match "KBR 001" (becomes "kbr001")
        // NOT "KBR 0012" or "KBR 002"
        if (appointmentNumNormalized === searchNormalized || tokenNumNormalized === searchNormalized) {
          score = 1000;
          matched = true;
        }
        // EXACT token match - as entered (score 999)
        else if (appointmentNum === searchLower || tokenNum === searchLower) {
          score = 999;
          matched = true;
        }
        // Exact phone match (score 950)
        else if (phone === searchQuery.trim()) {
          score = 950;
          matched = true;
        }
        // Exact name match (score 900)
        else if (patientName === searchLower) {
          score = 900;
          matched = true;
        }
        // Token starts with search - normalized (score 500)
        else if (appointmentNumNormalized.startsWith(searchNormalized) || tokenNumNormalized.startsWith(searchNormalized)) {
          score = 500;
          matched = true;
        }
        // Token starts with search - as entered (score 480)
        else if (appointmentNum.startsWith(searchLower) || tokenNum.startsWith(searchLower)) {
          score = 480;
          matched = true;
        }
        // Name starts with search (score 400)
        else if (patientName.startsWith(searchLower)) {
          score = 400;
          matched = true;
        }
        // Phone starts with search (score 350)
        else if (phone.startsWith(searchQuery.trim())) {
          score = 350;
          matched = true;
        }
        // Token contains search - normalized (score 200)
        else if (appointmentNumNormalized.includes(searchNormalized) || tokenNumNormalized.includes(searchNormalized)) {
          score = 200;
          matched = true;
        }
        // Token contains search - as entered (score 180)
        else if (appointmentNum.includes(searchLower) || tokenNum.includes(searchLower)) {
          score = 180;
          matched = true;
        }
        // Name contains search (score 150)
        else if (patientName.includes(searchLower)) {
          score = 150;
          matched = true;
        }
        // Phone contains search (score 100)
        else if (phone.includes(searchQuery.trim())) {
          score = 100;
          matched = true;
        }
        // Doctor name match (score 50)
        else if (doctorName.includes(searchLower)) {
          score = 50;
          matched = true;
        }
        // Department match (score 30)
        else if (department.includes(searchLower)) {
          score = 30;
          matched = true;
        }
        
        return { appointment, score, matched };
      });
      
      // Filter out non-matches and sort by relevance score (highest first)
      filtered = scoredResults
        .filter(item => item.matched)
        .sort((a, b) => {
          // Sort by score descending
          if (b.score !== a.score) return b.score - a.score;
          // If scores are equal, sort by appointment number
          return (a.appointment.appointmentNumber || '').localeCompare(b.appointment.appointmentNumber || '');
        })
        .map(item => item.appointment);
      
      // Remove debug logging after testing
      console.log('Search results ordered:', filtered.map(a => ({
        token: a.appointmentNumber,
        name: a.patientName
      })));
    }

    // Filter by status
    if (filterStatus && filterStatus !== 'All') {
      filtered = filtered.filter(appointment => appointment.status === filterStatus);
    }

    // Filter by date
    if (filterDate && filterDate !== 'All') {
      filtered = filtered.filter(appointment => checkDateFilter(appointment.date, filterDate));
    }

    setFilteredAppointments(filtered);
  }, [appointments, searchQuery, filterStatus, filterDate]);

  // Form validation
  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!newAppointmentForm.patientName.trim()) errors.patientName = 'Patient name is required';
    
    // Validate phone number - exactly 10 digits
    if (!newAppointmentForm.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else {
      const cleanPhone = newAppointmentForm.phone.replace(/\D/g, '');
      if (cleanPhone.length !== 10) {
        errors.phone = 'Please enter exactly 10 digits';
      } else if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
        errors.phone = 'Please enter a valid Indian mobile number starting with 6, 7, 8, or 9';
      }
    }
    
    if (!newAppointmentForm.age.trim()) errors.age = 'Age is required';
    if (!newAppointmentForm.gender) errors.gender = 'Gender is required';
    if (!newAppointmentForm.doctorId) errors.doctorId = 'Doctor selection is required';
    if (!newAppointmentForm.service) errors.service = 'Service selection is required';
    
    // Validate custom service if Others is selected
    if (newAppointmentForm.service === 'Others' && !newAppointmentForm.customService?.trim()) {
      errors.customService = 'Please specify the custom service name';
    }
    
    if (!newAppointmentForm.appointmentType) errors.appointmentType = 'Appointment type is required';
    if (!newAppointmentForm.date) errors.date = 'Date is required';
    if (!newAppointmentForm.time) errors.time = 'Time is required';
    
    // Fee amount validation - allow empty for test services that require "Contact Hospital"
    if (!newAppointmentForm.feeAmount.trim()) {
      // Only require fee amount for standard consultation services
      const isConsultationService = !newAppointmentForm.service || 
        newAppointmentForm.service.toLowerCase().includes('consultation') ||
        newAppointmentForm.service.toLowerCase().includes('checkup');
      
      if (isConsultationService) {
        errors.feeAmount = 'Fee amount is required for consultation services';
      }
      // For test services, we allow empty fee amounts (will show "Contact Hospital")
    }
    
    if (!newAppointmentForm.paymentMode) errors.paymentMode = 'Payment mode is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Get available services based on selected doctor's department
  const getAvailableServices = (): string[] => {
    if (!newAppointmentForm.department) return [];
    return firebaseServices[newAppointmentForm.department] || 
           defaultServices[newAppointmentForm.department as keyof typeof defaultServices] || 
           ['Consultation'];
  };

  // Reset form
  const resetForm = () => {
    setNewAppointmentForm({
      patientName: '',
      phone: '',
      age: '',
      gender: '',
      doctorId: '',
      department: '',
      service: '',
      appointmentType: '',
      date: null,
      time: null,
      symptoms: '',
      feeAmount: '',
      paymentMode: ''
    });
    setFormErrors({});
  };

  // Handle form submission
  const handleCreateAppointment = async () => {
    if (!validateForm()) return;

    setSubmittingForm(true);
    
    try {
      const selectedDoctor = doctors.find((d: any) => d.id === newAppointmentForm.doctorId);
      
      // Use custom service name if Others is selected
      const finalServiceName = newAppointmentForm.service === 'Others' 
        ? newAppointmentForm.customService || 'Other Service'
        : newAppointmentForm.service;
      
      // Prepare appointment data for Firebase
      const isRazorpay = newAppointmentForm.paymentMode === 'Razorpay';
      const paymentStatus = isRazorpay ? 'Pending' : 'Pending';
      const paymentMethod = isRazorpay ? 'Razorpay' : newAppointmentForm.paymentMode;
      
      const appointmentData = {
        patientName: newAppointmentForm.patientName,
        patientPhone: newAppointmentForm.phone,
        contactNumber: newAppointmentForm.phone,
        phone: newAppointmentForm.phone,
        age: parseInt(newAppointmentForm.age),
        gender: newAppointmentForm.gender,
        doctorName: selectedDoctor?.name || '',
        doctorId: newAppointmentForm.doctorId,
        doctor: selectedDoctor?.name || '',
        department: newAppointmentForm.department,
        service: finalServiceName,
        serviceName: finalServiceName,
        type: newAppointmentForm.appointmentType,
        appointmentType: newAppointmentForm.appointmentType,
        appointmentDate: newAppointmentForm.date?.toISOString().split('T')[0] || '',
        date: newAppointmentForm.date?.toISOString().split('T')[0] || '',
        time: newAppointmentForm.time?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }) || '',
        appointmentTime: newAppointmentForm.time?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }) || '',
        status: 'scheduled',
        symptoms: newAppointmentForm.symptoms,
        reason: newAppointmentForm.symptoms,
        fee: newAppointmentForm.feeAmount ? parseInt(newAppointmentForm.feeAmount) : null,
        amount: newAppointmentForm.feeAmount ? parseInt(newAppointmentForm.feeAmount) : null,
        paymentMode: newAppointmentForm.paymentMode,
        paymentMethod: paymentMethod,
        paymentStatus: paymentStatus,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // If Razorpay payment, initiate payment first
      if (isRazorpay && newAppointmentForm.feeAmount && parseInt(newAppointmentForm.feeAmount) > 0) {
        await handleRazorpayPaymentForNewAppointment(appointmentData);
      } else {
        // Create appointment directly for other payment modes
        const result = await AppointmentManagementService.createAppointment(appointmentData);
        
        if (result.success) {
          setShowAddModal(false);
          resetForm();
          
          setSnackbar({
            open: true,
            message: 'Appointment created successfully!',
            severity: 'success'
          });
        }
      }
    } catch (error: any) {
      console.error('Error creating appointment:', error);
      setSnackbar({
        open: true,
        message: error?.message || 'Failed to create appointment. Please try again.',
        severity: 'error'
      });
    } finally {
      setSubmittingForm(false);
    }
  };

  // Handle Razorpay payment for new appointment
  const handleRazorpayPaymentForNewAppointment = async (appointmentData: any) => {
    try {
      const amount = parseInt(newAppointmentForm.feeAmount);

      // Prepare payment data
      const paymentData = {
        amount: amount,
        patientId: appointmentData.phone || Date.now().toString(),
        patientName: appointmentData.patientName,
        patientEmail: '',
        patientPhone: appointmentData.phone,
        paymentType: 'appointment',
        description: `${appointmentData.serviceName} - ${appointmentData.doctorName}`
      };

      // Open Razorpay checkout with callbacks
      await RazorpayService.initiatePayment(
        paymentData,
        // Success callback
        async (response: RazorpayResponse) => {
          console.log('✅ Payment successful:', response);
          
          // Payment successful, create appointment with payment details
          const updatedAppointmentData = {
            ...appointmentData,
            paymentStatus: 'Paid',
            razorpayOrderId: response.razorpay_order_id || '',
            razorpayPaymentId: response.razorpay_payment_id,
            transactionId: response.razorpay_payment_id
          };

          const result = await AppointmentManagementService.createAppointment(updatedAppointmentData);
          
          if (result.success) {
            // Generate invoice for the payment
            try {
              console.log('📄 Generating invoice for Razorpay payment...');
              const invoicePaymentData = {
                patientId: appointmentData.phone || updatedAppointmentData.patientId,
                patientName: appointmentData.patientName,
                amount: amount,
                paymentMethod: 'Razorpay Online Payment',
                transactionId: response.razorpay_payment_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpayOrderId: response.razorpay_order_id || '',
                razorpaySignature: response.razorpay_signature || '',
                serviceName: appointmentData.serviceName,
                description: `Appointment with ${appointmentData.doctorName} - ${appointmentData.serviceName}`,
                items: [{
                  name: appointmentData.serviceName || 'Consultation Fee',
                  description: `Appointment with ${appointmentData.doctorName}`,
                  quantity: 1,
                  rate: amount,
                  amount: amount
                }]
              };
              
              await generateInvoiceForPayment(response.razorpay_payment_id, invoicePaymentData, db);
              console.log('✅ Invoice generated successfully for appointment');
            } catch (invoiceError) {
              console.error('⚠️ Payment successful but invoice generation failed:', invoiceError);
              // Don't block the success flow
            }
            
            setShowAddModal(false);
            resetForm();
            setSubmittingForm(false);
            
            setSnackbar({
              open: true,
              message: 'Payment successful! Appointment booked.',
              severity: 'success'
            });
          }
        },
        // Failure callback
        (error: RazorpayError) => {
          console.error('❌ Payment failed:', error);
          setSubmittingForm(false);
          
          setSnackbar({
            open: true,
            message: error.description || 'Payment failed. Please try again.',
            severity: 'error'
          });
        }
      );
    } catch (error: any) {
      console.error('Payment error:', error);
      setSubmittingForm(false);
      
      setSnackbar({
        open: true,
        message: error?.message || 'Failed to initiate payment. Please try again.',
        severity: 'error'
      });
    }
  };

  // Handle edit appointment
  const handleEditAppointment = async () => {
    if (!validateForm() || !selectedAppointment) return;
    
    setSubmittingForm(true);
    
    try {
      // Find selected doctor
      const selectedDoctor = doctors.find(d => d.id === newAppointmentForm.doctorId);
      
      // Prepare updated appointment data
      const updatedAppointmentData = {
        patientName: newAppointmentForm.patientName,
        name: newAppointmentForm.patientName,
        contactNumber: newAppointmentForm.phone,
        patientPhone: newAppointmentForm.phone,
        phone: newAppointmentForm.phone,
        age: parseInt(newAppointmentForm.age),
        patientAge: parseInt(newAppointmentForm.age),
        gender: newAppointmentForm.gender,
        patientGender: newAppointmentForm.gender,
        doctorId: newAppointmentForm.doctorId,
        doctorName: selectedDoctor?.name || '',
        doctor: selectedDoctor?.name || '',
        department: newAppointmentForm.department,
        specialty: newAppointmentForm.department,
        service: newAppointmentForm.service,
        serviceName: newAppointmentForm.service,
        serviceType: newAppointmentForm.service,
        type: newAppointmentForm.appointmentType,
        appointmentType: newAppointmentForm.appointmentType,
        appointmentDate: newAppointmentForm.date?.toISOString().split('T')[0] || '',
        date: newAppointmentForm.date?.toISOString().split('T')[0] || '',
        time: newAppointmentForm.time?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }) || '',
        appointmentTime: newAppointmentForm.time?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }) || '',
        symptoms: newAppointmentForm.symptoms,
        reason: newAppointmentForm.symptoms,
        fee: newAppointmentForm.feeAmount ? parseInt(newAppointmentForm.feeAmount) : null,
        fees: newAppointmentForm.feeAmount ? parseInt(newAppointmentForm.feeAmount) : null,
        amount: newAppointmentForm.feeAmount ? parseInt(newAppointmentForm.feeAmount) : null,
        paymentMode: newAppointmentForm.paymentMode,
        updatedAt: new Date().toISOString()
      };
      
      // Update appointment in Firebase
      await AppointmentManagementService.updateAppointment(selectedAppointment.id, updatedAppointmentData);
      
      setShowEditModal(false);
      setSelectedAppointment(null);
      resetForm();
      
      setSnackbar({
        open: true,
        message: 'Appointment updated successfully!',
        severity: 'success'
      });
    } catch (error: any) {
      console.error('Error updating appointment:', error);
      setSnackbar({
        open: true,
        message: error?.message || 'Failed to update appointment. Please try again.',
        severity: 'error'
      });
    } finally {
      setSubmittingForm(false);
    }
  };

  // Handle collect payment button click
  const handleCollectPayment = (appointment: any) => {
    // Don't allow payment collection for appointments without fee amounts
    if (!appointment.feeAmount) {
      setSnackbar({
        open: true,
        message: 'Cannot collect payment - Please set the fee amount first',
        severity: 'warning'
      });
      return;
    }
    
    setSelectedPaymentAppointment(appointment);
    setPaymentAmount(String(appointment.feeAmount || ''));
    setShowPaymentDialog(true);
  };

  // Handle cash payment
  const handleCashPayment = () => {
    setShowPaymentDialog(false);
    setShowCashConfirmDialog(true);
  };

  // Confirm cash payment
  const confirmCashPayment = async () => {
    if (!selectedPaymentAppointment) return;

    try {
      setProcessingPayment(true);
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      const amount = parseFloat(paymentAmount);
      
      // Update appointment payment status - NO transaction ID for cash
      const appointmentRef = doc(db, 'appointments', selectedPaymentAppointment.id);
      await updateDoc(appointmentRef, {
        paymentStatus: 'Pending Verification',
        paymentMethod: 'Cash',
        // NO transactionId for cash payments
        paymentCollectedBy: currentUser?.email || 'admin',
        paymentCollectedAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      // Create payment record in payments collection WITHOUT transaction ID for cash
      const paymentRecord = {
        patientId: selectedPaymentAppointment.patientId || selectedPaymentAppointment.id,
        patientName: selectedPaymentAppointment.patientName,
        patientPhone: selectedPaymentAppointment.phone || selectedPaymentAppointment.patientPhone,
        amount: amount,
        fullAmount: amount,
        totalAmount: amount,
        actualAmountPaid: amount,
        paidAmount: amount,
        dueAmount: 0,
        remainingAmount: 0,
        type: 'appointment',
        method: 'Cash',
        paymentMethod: 'Cash',
        description: `Appointment fee - ${selectedPaymentAppointment.service || 'Consultation'} - ${selectedPaymentAppointment.patientName}`,
        serviceName: selectedPaymentAppointment.service || 'Consultation',
        // NO transactionId for cash payments
        status: 'Pending Verification',
        paymentStatus: 'Pending Verification',
        statusColor: '#F59E0B',
        patientType: 'OP',
        serviceType: 'OP',
        appointmentId: selectedPaymentAppointment.id,
        appointmentNumber: selectedPaymentAppointment.appointmentNumber || selectedPaymentAppointment.tokenNumber,
        lastPaymentDate: new Date().toISOString().split('T')[0],
        paymentDate: Timestamp.now(),
        registrationDate: new Date().toISOString().split('T')[0],
        collectedBy: currentUser?.email || 'admin',
        collectedByUid: currentUser?.uid || '',
        collectedAt: Timestamp.now(),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        transactionHistory: [{
          type: 'appointment',
          amount: amount,
          date: new Date().toISOString().split('T')[0],
          method: 'Cash',
          status: 'Pending Verification',
          // NO transactionId for cash
          collectedBy: currentUser?.email || 'admin'
        }]
      };

      await addDoc(collection(db, 'payments'), paymentRecord);
      console.log('✅ Payment record created in payments collection (Cash - Pending Verification)');

      setSnackbar({
        open: true,
        message: `Cash payment of ₹${paymentAmount} recorded. Awaiting admin verification.`,
        severity: 'success'
      });

      setShowCashConfirmDialog(false);
      setSelectedPaymentAppointment(null);
      setPaymentAmount('');
    } catch (error: any) {
      console.error('Error updating payment:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update payment status',
        severity: 'error'
      });
    } finally {
      setProcessingPayment(false);
    }
  };

  // Handle Razorpay payment for existing appointment
  const handleRazorpayPaymentForExistingAppointment = async () => {
    if (!selectedPaymentAppointment) return;

    setProcessingPayment(true);
    setShowPaymentDialog(false);

    try {
      const amount = parseFloat(paymentAmount);
      if (amount <= 0 || isNaN(amount)) {
        setSnackbar({
          open: true,
          message: 'Please enter a valid amount',
          severity: 'error'
        });
        setProcessingPayment(false);
        return;
      }

      const paymentData = {
        amount: amount,
        patientId: selectedPaymentAppointment.patientId || selectedPaymentAppointment.id,
        patientName: selectedPaymentAppointment.patientName,
        patientEmail: selectedPaymentAppointment.email || '',
        patientPhone: selectedPaymentAppointment.phone || '',
        paymentType: 'appointment',
        description: `Appointment fee - ${selectedPaymentAppointment.service || 'Consultation'}`
      };

      await RazorpayService.initiatePayment(
        paymentData,
        // Success callback
        async (response: RazorpayResponse) => {
          console.log('✅ Razorpay payment successful:', response);

          try {
            const auth = getAuth();
            const currentUser = auth.currentUser;
            
            // Update appointment payment status - Pending Verification
            const appointmentRef = doc(db, 'appointments', selectedPaymentAppointment.id);
            await updateDoc(appointmentRef, {
              paymentStatus: 'Pending Verification',
              paymentMethod: 'Razorpay',
              transactionId: response.razorpay_payment_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id || '',
              razorpaySignature: response.razorpay_signature || '',
              paymentCollectedBy: currentUser?.email || 'admin',
              paymentCollectedAt: Timestamp.now(),
              updatedAt: Timestamp.now()
            });

            // Create payment record in payments collection
            const paymentRecord = {
              patientId: selectedPaymentAppointment.patientId || selectedPaymentAppointment.id,
              patientName: selectedPaymentAppointment.patientName,
              patientPhone: selectedPaymentAppointment.phone || selectedPaymentAppointment.patientPhone,
              amount: amount,
              fullAmount: amount,
              totalAmount: amount,
              actualAmountPaid: amount,
              paidAmount: amount,
              dueAmount: 0,
              remainingAmount: 0,
              type: 'appointment',
              method: 'Razorpay',
              paymentMethod: 'Razorpay',
              description: `Appointment fee - ${selectedPaymentAppointment.service || 'Consultation'} - ${selectedPaymentAppointment.patientName}`,
              serviceName: selectedPaymentAppointment.service || 'Consultation',
              transactionId: response.razorpay_payment_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id || '',
              razorpaySignature: response.razorpay_signature || '',
              status: 'Pending Verification',
              paymentStatus: 'Pending Verification',
              statusColor: '#F59E0B',
              patientType: 'OP',
              serviceType: 'OP',
              appointmentId: selectedPaymentAppointment.id,
              appointmentNumber: selectedPaymentAppointment.appointmentNumber || selectedPaymentAppointment.tokenNumber,
              lastPaymentDate: new Date().toISOString().split('T')[0],
              paymentDate: Timestamp.now(),
              registrationDate: new Date().toISOString().split('T')[0],
              collectedBy: currentUser?.email || 'admin',
              collectedByUid: currentUser?.uid || '',
              collectedAt: Timestamp.now(),
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now(),
              transactionHistory: [{
                type: 'appointment',
                amount: amount,
                date: new Date().toISOString().split('T')[0],
                method: 'Razorpay',
                status: 'Pending Verification',
                transactionId: response.razorpay_payment_id,
                razorpayPaymentId: response.razorpay_payment_id,
                collectedBy: currentUser?.email || 'admin'
              }]
            };

            await addDoc(collection(db, 'payments'), paymentRecord);
            console.log('✅ Payment record created in payments collection (Razorpay - Pending Verification)');

            setSnackbar({
              open: true,
              message: `Payment of ₹${amount} received via Razorpay! Transaction ID: ${response.razorpay_payment_id}. Awaiting admin verification.`,
              severity: 'success'
            });

            setSelectedPaymentAppointment(null);
            setPaymentAmount('');
            setProcessingPayment(false);
          } catch (error: any) {
            console.error('Error updating payment:', error);
            setSnackbar({
              open: true,
              message: 'Payment successful but failed to update record',
              severity: 'warning'
            });
            setProcessingPayment(false);
          }
        },
        // Failure callback
        (error: RazorpayError) => {
          console.error('❌ Razorpay payment failed:', error);
          setSnackbar({
            open: true,
            message: `Payment failed: ${error.description || 'Please try again'}`,
            severity: 'error'
          });
          setProcessingPayment(false);
        }
      );
    } catch (error: any) {
      console.error('Error initiating payment:', error);
      setSnackbar({
        open: true,
        message: 'Failed to initiate payment',
        severity: 'error'
      });
      setProcessingPayment(false);
    }
  };

  // Handle status menu
  const handleStatusMenuClick = (event: React.MouseEvent<HTMLElement>, appointment: any) => {
    setStatusMenuAnchor(event.currentTarget);
    setStatusMenuAppointment(appointment);
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchor(null);
    setStatusMenuAppointment(null);
  };

  const handleStatusChange = (newStatus: string) => {
    setPendingStatusChange(newStatus);
    setShowStatusConfirmDialog(true);
    // Don't close menu or clear appointment yet - keep it for confirmation
    setStatusMenuAnchor(null); // Just close the menu visually
  };

  const confirmStatusChange = async () => {
    if (!statusMenuAppointment || !pendingStatusChange) {
      console.log('❌ Missing data:', { statusMenuAppointment, pendingStatusChange });
      return;
    }

    // If changing to Completed, check if payment is completed
    if (pendingStatusChange === 'Completed') {
      const paymentStatus = statusMenuAppointment.paymentStatus;
      const paymentMode = statusMenuAppointment.paymentMode || statusMenuAppointment.paymentMethod;
      
      if (paymentMode && paymentMode.toLowerCase().includes('pay at hospital') && paymentStatus !== 'Completed' && paymentStatus !== 'Paid') {
        setSnackbar({
          open: true,
          message: 'Please collect payment before marking appointment as completed',
          severity: 'warning'
        });
        setShowStatusConfirmDialog(false);
        setPendingStatusChange('');
        setStatusMenuAppointment(null);
        return;
      }
    }

    try {
      const statusMap: { [key: string]: string } = {
        'Pending': 'pending',
        'Confirm': 'confirmed',
        'Completed': 'completed'
      };

      const firebaseStatus = statusMap[pendingStatusChange] || pendingStatusChange.toLowerCase();
      await AppointmentManagementService.updateAppointmentStatus(statusMenuAppointment.id, firebaseStatus);

      setSnackbar({
        open: true,
        message: `Appointment status updated to ${pendingStatusChange}`,
        severity: 'success'
      });

      setShowStatusConfirmDialog(false);
      setPendingStatusChange('');
      setStatusMenuAppointment(null); // Clear appointment after successful update
    } catch (error: any) {
      console.error('Error updating status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update status',
        severity: 'error'
      });
    }
  };

  // Verify payment - change from 'Pending Verification' to 'Fully Paid'
  const handleVerifyPayment = async (appointment: any) => {
    try {
      console.log('🔍 Starting payment verification for appointment:', appointment.id);
      console.log('📋 Appointment data:', {
        id: appointment.id,
        patientId: appointment.patientId,
        patientName: appointment.patientName || appointment.name,
        paymentStatus: appointment.paymentStatus
      });

      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!appointment.id) {
        throw new Error('Appointment ID is missing');
      }
      
      // Update appointment payment status to Fully Paid
      const appointmentRef = doc(db, 'appointments', appointment.id);
      await updateDoc(appointmentRef, {
        paymentStatus: 'Fully Paid',
        verifiedBy: currentUser?.email || 'admin',
        verifiedAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      console.log('✅ Appointment payment status updated to Fully Paid');

      // Find and update payment record in payments collection
      const paymentsQuery = query(
        collection(db, 'payments'),
        where('appointmentId', '==', appointment.id)
      );
      const paymentsSnapshot = await getDocs(paymentsQuery);
      console.log(`📊 Found ${paymentsSnapshot.docs.length} payment(s) for appointment ${appointment.id}`);
      
      if (paymentsSnapshot.empty) {
        console.warn('⚠️ No payment records found for appointment:', appointment.id);
        throw new Error('No payment record found for this appointment');
      }
      
      // Check if there's a pending invoice for this appointment (only if patientId exists)
      let pendingInvoiceId = null;
      if (appointment.patientId) {
        const invoicesQuery = query(
          collection(db, 'invoices'),
          where('patientId', '==', appointment.patientId),
          where('status', '==', 'Pending')
        );
        const invoicesSnapshot = await getDocs(invoicesQuery);
        console.log(`📄 Found ${invoicesSnapshot.docs.length} pending invoice(s) for patient`);
        
        for (const invoiceDoc of invoicesSnapshot.docs) {
          const invoiceData = invoiceDoc.data();
          // Check if invoice description matches this appointment
          if (invoiceData.description?.includes(appointment.doctorName) || 
              invoiceData.description?.includes(appointment.serviceName)) {
            pendingInvoiceId = invoiceDoc.id;
            console.log('✅ Found matching pending invoice:', pendingInvoiceId);
            break;
          }
        }
      } else {
        console.log('ℹ️ No patientId available, skipping invoice check');
      }
      
      // Generate invoice for each payment
      for (const paymentDoc of paymentsSnapshot.docs) {
        const paymentRef = doc(db, 'payments', paymentDoc.id);
        const paymentData = paymentDoc.data();
        
        console.log('💳 Processing payment:', paymentDoc.id, paymentData);
        
        await updateDoc(paymentRef, {
          status: 'Fully Paid',
          paymentStatus: 'Fully Paid',
          statusColor: '#22C55E',
          verifiedBy: currentUser?.email || 'admin',
          verifiedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        console.log('✅ Payment record updated to Fully Paid:', paymentDoc.id);
        
        // If pending invoice exists, update it; otherwise create new one
        try {
          if (pendingInvoiceId) {
            console.log('🔄 Updating existing pending invoice:', pendingInvoiceId);
            const invoiceRef = doc(db, 'invoices', pendingInvoiceId);
            await updateDoc(invoiceRef, {
              status: 'Paid',
              paymentMethod: paymentData.paymentMethod || 'Cash',
              transactionId: paymentData.transactionId || paymentData.razorpayPaymentId || `CASH-${Date.now()}`,
              razorpayPaymentId: paymentData.razorpayPaymentId || null,
              razorpayOrderId: paymentData.razorpayOrderId || null,
              razorpaySignature: paymentData.razorpaySignature || null,
              notes: `Payment received on ${new Date().toLocaleDateString('en-IN')} at ${new Date().toLocaleTimeString()}`,
              verifiedBy: currentUser?.email || 'admin',
              verifiedAt: Timestamp.now(),
              updatedAt: Timestamp.now()
            });
            console.log('✅ Pending invoice updated to Paid:', pendingInvoiceId);
          } else {
            console.log('🧾 Generating new invoice for verified payment...');
            await generateInvoiceForPayment(paymentDoc.id, paymentData, db);
            console.log('✅ Invoice generated for payment:', paymentDoc.id);
          }
        } catch (invoiceError) {
          console.error('❌ Error generating/updating invoice:', invoiceError);
          // Don't fail verification if invoice generation fails
        }
      }

      setSnackbar({
        open: true,
        message: `Payment verified and invoice ${pendingInvoiceId ? 'updated' : 'generated'} for ${appointment.patientName}`,
        severity: 'success'
      });
    } catch (error: any) {
      console.error('❌ Error verifying payment:', error);
      console.error('Error details:', error.message, error.stack);
      setSnackbar({
        open: true,
        message: `Failed to verify payment: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    }
  };

  // Handle appointment actions
  const handleAppointmentAction = async (action: string, appointment: any) => {
    setAnchorEl(null);
    setActionMenuAppointment(null);

    console.log('🎯 handleAppointmentAction called:', {
      action,
      appointmentId: appointment?.id,
      appointmentObject: appointment
    });

    try {
      switch (action) {
        case 'view':
          setSelectedAppointment(appointment);
          setShowDetailsModal(true);
          break;
        
        case 'edit':
          setSelectedAppointment(appointment);
          // Pre-populate the form with appointment data
          setNewAppointmentForm({
            patientName: appointment.patientName || '',
            phone: appointment.phone || '',
            age: String(appointment.age || ''),
            gender: appointment.gender || '',
            doctorId: appointment.doctorId || '',
            department: appointment.department || '',
            service: appointment.service || '',
            appointmentType: appointment.appointmentType || '',
            date: appointment.date ? new Date(appointment.date) : null,
            time: appointment.time ? new Date(`2000-01-01 ${appointment.time}`) : null,
            symptoms: appointment.symptoms || '',
            feeAmount: String(appointment.feeAmount || ''),
            paymentMode: appointment.paymentMode || ''
          });
          setShowEditModal(true);
          break;
        
        case 'admit':
          // Navigate to patients page with pre-filled appointment data
          navigate('/patients', {
            state: {
              fromAppointment: true,
              appointmentData: {
                appointmentId: appointment.id,
                patientName: appointment.patientName || '',
                age: String(appointment.age || ''),
                gender: appointment.gender || 'Male',
                phone: appointment.phone || '',
                emergencyContact: appointment.emergencyContact || '',
                address: appointment.patientAddress || '',
                doctor: appointment.doctorName || '',
                department: appointment.department || '',
                symptoms: appointment.symptoms || '',
                feeAmount: String(appointment.feeAmount || ''),
                paymentMode: appointment.paymentMode || 'Cash',
                service: appointment.service || '',
                appointmentType: appointment.appointmentType || '',
                date: appointment.date || '',
                time: appointment.time || ''
              }
            }
          });
          break;
        
        case 'confirm':
          console.log('🔄 Confirming appointment:', {
            id: appointment.id,
            idLength: appointment.id?.length,
            fullAppointment: appointment
          });
          await AppointmentManagementService.updateAppointmentStatus(appointment.id, 'confirmed');
          // Real-time listener will automatically update the UI
          setSnackbar({
            open: true,
            message: 'Appointment confirmed successfully!',
            severity: 'success'
          });
          break;
        
        case 'cancel':
          console.log('🔄 Cancelling appointment:', {
            id: appointment.id,
            idLength: appointment.id?.length,
            fullAppointment: appointment
          });
          await AppointmentManagementService.updateAppointmentStatus(appointment.id, 'cancelled');
          // Real-time listener will automatically update the UI
          setSnackbar({
            open: true,
            message: 'Appointment cancelled successfully!',
            severity: 'warning'
          });
          break;
        
        case 'complete':
          console.log('🔄 Completing appointment:', {
            id: appointment.id,
            idLength: appointment.id?.length,
            fullAppointment: appointment
          });
          await AppointmentManagementService.updateAppointmentStatus(appointment.id, 'completed');
          // Real-time listener will automatically update the UI
          setSnackbar({
            open: true,
            message: 'Appointment marked as completed!',
            severity: 'success'
          });
          break;
      }
    } catch (error: any) {
      console.error('❌ Error updating appointment:', error);
      
      // Check if it's a "document not found" error
      if (error?.message?.includes('No document to update')) {
        setSnackbar({
          open: true,
          message: 'This appointment no longer exists. Refreshing list...',
          severity: 'warning'
        });
        // Reload appointments to sync with Firebase
        await loadAppointmentsData();
      } else {
        setSnackbar({
          open: true,
          message: error?.message || 'Failed to update appointment',
          severity: 'error'
        });
      }
    }
  };

  // Get status color (matching mobile app)
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Confirmed': return '#10B981';
      case 'Admitted': return '#059669';
      case 'Pending': return '#F59E0B';
      case 'Completed': return '#6B7280';
      case 'Cancelled': return '#EF4444';
      default: return '#6B7280';
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Confirmed': return <CheckCircleIcon sx={{ fontSize: 16 }} />;
      case 'Admitted': return <LocalHospitalIcon sx={{ fontSize: 16 }} />;
      case 'Pending': return <PendingIcon sx={{ fontSize: 16 }} />;
      case 'Completed': return <CheckCircleIcon sx={{ fontSize: 16 }} />;
      case 'Cancelled': return <CancelIcon sx={{ fontSize: 16 }} />;
      default: return <PendingIcon sx={{ fontSize: 16 }} />;
    }
  };

  // Show loading state
  if (loadingData) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        gap: 2
      }}>
        <CircularProgress size={48} />
        <Typography variant="body1" color="textSecondary">
          Loading appointments data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1A202C', mb: 1 }}>
          Appointments Management
        </Typography>
        <Typography variant="body1" sx={{ color: '#718096' }}>
          Manage and track all patient appointments efficiently
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mb: 4 }}>
        <Card sx={{ p: 3, borderLeft: '4px solid #3B82F6' }}>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>Total Appointments</Typography>
          <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#3B82F6' }}>
            {appointmentStats.total}
          </Typography>
          <Typography variant="caption" sx={{ color: '#9CA3AF' }}>All appointments</Typography>
        </Card>
        
        <Card sx={{ p: 3, borderLeft: '4px solid #10B981' }}>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>Today</Typography>
          <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#10B981' }}>
            {appointmentStats.today}
          </Typography>
          <Typography variant="caption" sx={{ color: '#9CA3AF' }}>Scheduled today</Typography>
        </Card>
        
        <Card sx={{ p: 3, borderLeft: '4px solid #8B5CF6' }}>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>Confirmed</Typography>
          <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#8B5CF6' }}>
            {appointmentStats.confirmed}
          </Typography>
          <Typography variant="caption" sx={{ color: '#9CA3AF' }}>Ready to serve</Typography>
        </Card>
        
        <Card sx={{ p: 3, borderLeft: '4px solid #059669' }}>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>Admitted</Typography>
          <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#059669' }}>
            {appointmentStats.admitted}
          </Typography>
          <Typography variant="caption" sx={{ color: '#9CA3AF' }}>In hospital</Typography>
        </Card>
        
        <Card sx={{ p: 3, borderLeft: '4px solid #F59E0B' }}>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>Pending</Typography>
          <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#F59E0B' }}>
            {appointmentStats.pending}
          </Typography>
          <Typography variant="caption" sx={{ color: '#9CA3AF' }}>Need confirmation</Typography>
        </Card>
        
        <Card sx={{ p: 3, borderLeft: '4px solid #2196F3' }}>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>Completed</Typography>
          <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#2196F3' }}>
            {appointmentStats.completed}
          </Typography>
          <Typography variant="caption" sx={{ color: '#9CA3AF' }}>Finished</Typography>
        </Card>
      </Box>

      {/* Controls */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Search and Action Buttons Row */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 2, flex: 1, width: '100%', flexWrap: 'wrap' }}>
              <TextField
                placeholder="Search by Token (KBR 001), Name, or Phone..."
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: '#9CA3AF', mr: 1 }} />
                }}
                sx={{ minWidth: 250, flex: 1 }}
              />
              
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Confirmed">Confirmed</MenuItem>
                  <MenuItem value="Admitted">Admitted</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadAppointmentsData}
                disabled={loadingData}
                sx={{ 
                  borderColor: '#3B82F6',
                  color: '#3B82F6',
                  '&:hover': { 
                    borderColor: '#2563EB',
                    backgroundColor: 'rgba(59, 130, 246, 0.04)'
                  },
                  whiteSpace: 'nowrap'
                }}
              >
                Refresh
              </Button>
            </Box>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowAddModal(true)}
              sx={{ 
                backgroundColor: '#3B82F6',
                '&:hover': { backgroundColor: '#2563EB' },
                whiteSpace: 'nowrap'
              }}
            >
              Add Appointment
            </Button>
          </Box>

          {/* Date Filter Chips Row */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pt: 1 }}>
            {['All', 'Today', 'Tomorrow', 'This Week', 'This Month'].map((dateFilter) => (
              <Chip
                key={dateFilter}
                label={dateFilter}
                onClick={() => setFilterDate(dateFilter)}
                color={filterDate === dateFilter ? 'primary' : 'default'}
                variant={filterDate === dateFilter ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: filterDate === dateFilter ? 600 : 400,
                  borderColor: filterDate === dateFilter ? '#3B82F6' : '#E5E7EB',
                  '&:hover': {
                    backgroundColor: filterDate === dateFilter ? '#2563EB' : 'rgba(59, 130, 246, 0.08)'
                  }
                }}
              />
            ))}
          </Box>
        </Box>
      </Card>

      {/* Appointments List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {filteredAppointments.length === 0 ? (
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: '#6B7280', mb: 1 }}>
              No appointments found
            </Typography>
            <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
              {searchQuery || filterStatus !== 'All' 
                ? 'Try adjusting your search or filter criteria'
                : 'Start by adding a new appointment'
              }
            </Typography>
          </Card>
        ) : (
          filteredAppointments.map((appointment) => {
            // Debug logging for payment fields
            console.log('💳 Appointment payment info:', {
              id: appointment.id,
              name: appointment.patientName,
              paymentMode: appointment.paymentMode,
              paymentMethod: appointment.paymentMethod,
              paymentStatus: appointment.paymentStatus,
              shouldShowButton: ((appointment.paymentMode && appointment.paymentMode.toLowerCase().includes('pay at hospital')) || 
                                (appointment.paymentMethod && appointment.paymentMethod.toLowerCase().includes('pay at hospital'))) && 
                               appointment.paymentStatus !== 'Completed' && 
                               appointment.paymentStatus !== 'Paid'
            });
            
            return (
            <Card 
              key={appointment.id} 
              sx={{ 
                p: 0,
                overflow: 'hidden',
                border: '1px solid #E5E7EB',
                '&:hover': { 
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  borderColor: '#3B82F6'
                },
                transition: 'all 0.2s'
              }}
            >
              <Box sx={{ p: 3 }}>
                {/* Header Row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: '#3B82F6',
                        width: 56,
                        height: 56,
                        fontSize: '1.5rem',
                        fontWeight: 600
                      }}
                    >
                      {appointment.patientName.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1F2937', mb: 0.5 }}>
                        {appointment.patientName}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.875rem' }}>
                        {appointment.appointmentNumber || appointment.tokenNumber || 'N/A'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                        {appointment.age || 'N/A'} • {appointment.gender || 'N/A'} • {appointment.phone}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={appointment.status}
                      sx={{
                        backgroundColor: getStatusColor(appointment.status),
                        color: '#FFFFFF',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        height: 32
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => handleStatusMenuClick(e, appointment)}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                </Box>

                {/* Info Grid */}
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(2, 1fr)', 
                  gap: 2,
                  mb: 2,
                  pb: 2,
                  borderBottom: '1px solid #F3F4F6'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon sx={{ color: '#6B7280', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: '#4B5563' }}>
                      {appointment.doctorName}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalHospitalIcon sx={{ color: '#6B7280', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: '#4B5563' }}>
                      {appointment.department}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EventIcon sx={{ color: '#6B7280', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 500 }}>
                      {appointment.date} {appointment.time}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PaymentIcon sx={{ color: '#10B981', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: '#10B981', fontWeight: 600 }}>
                      {appointment.feeAmount ? `₹${appointment.feeAmount}` : 'Contact Hospital'} • {appointment.paymentStatus || 'Paid'}
                    </Typography>
                  </Box>
                </Box>

                {/* Service Type */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box 
                      sx={{ 
                        bgcolor: '#DBEAFE', 
                        color: '#1E40AF',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.875rem',
                        fontWeight: 500
                      }}
                    >
                      {appointment.service}
                    </Box>
                  </Box>
                  
                  {appointment.symptoms && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 0.5 }}>
                        Symptoms:
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#4B5563' }}>
                        {appointment.symptoms}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Booking Date Footer */}
                {appointment.bookingDate && (
                  <Typography variant="caption" sx={{ color: '#9CA3AF', display: 'block', mb: 2 }}>
                    Booked on: {appointment.bookingDate}
                  </Typography>
                )}

                {/* Verify Payment Button - Show for Pending Verification */}
                {appointment.paymentStatus === 'Pending Verification' && (
                  <Box sx={{ mb: 2 }}>
                    <Button
                      startIcon={<CheckCircleIcon />}
                      variant="contained"
                      size="medium"
                      fullWidth
                      onClick={() => handleVerifyPayment(appointment)}
                      sx={{
                        backgroundColor: '#F59E0B',
                        color: '#FFFFFF',
                        fontWeight: 600,
                        boxShadow: '0 4px 6px rgba(245, 158, 11, 0.2)',
                        '&:hover': {
                          backgroundColor: '#D97706',
                          boxShadow: '0 6px 8px rgba(245, 158, 11, 0.3)'
                        }
                      }}
                    >
                      Verify Payment ({appointment.feeAmount ? `₹${appointment.feeAmount}` : 'Contact Hospital'})
                    </Button>
                  </Box>
                )}

                {/* Collect Payment Button - Show for Pay at Hospital OR Pending payments */}
                {(
                  ((appointment.paymentMode && appointment.paymentMode.toLowerCase().includes('pay at hospital')) || 
                   (appointment.paymentMethod && appointment.paymentMethod.toLowerCase().includes('pay at hospital')) ||
                   appointment.paymentStatus === 'Pending') && 
                  appointment.paymentStatus !== 'Completed' && 
                  appointment.paymentStatus !== 'Paid' &&
                  appointment.paymentStatus !== 'Fully Paid' &&
                  appointment.paymentStatus !== 'Pending Verification' &&
                  appointment.feeAmount  // Only show if fee amount is available
                ) && (
                  <Box sx={{ mb: 2 }}>
                    <Button
                      startIcon={<CurrencyRupeeIcon />}
                      variant="contained"
                      size="medium"
                      fullWidth
                      onClick={() => handleCollectPayment(appointment)}
                      sx={{
                        backgroundColor: '#10B981',
                        color: '#FFFFFF',
                        fontWeight: 600,
                        boxShadow: '0 4px 6px rgba(16, 185, 129, 0.2)',
                        '&:hover': {
                          backgroundColor: '#059669',
                          boxShadow: '0 6px 8px rgba(16, 185, 129, 0.3)'
                        }
                      }}
                    >
                      Collect Payment (₹{appointment.feeAmount})
                    </Button>
                  </Box>
                )}

                {/* Contact Hospital Message for appointments without fee amount */}
                {!appointment.feeAmount && (
                  <Box sx={{ mb: 2 }}>
                    <Alert 
                      severity="info" 
                      sx={{
                        backgroundColor: '#FEF3C7',
                        borderLeft: '4px solid #F59E0B',
                        '& .MuiAlert-message': {
                          color: '#92400E',
                          fontWeight: 500
                        }
                      }}
                    >
                      <Typography variant="body2">
                        <strong>Contact Hospital:</strong> This test/service requires custom pricing. 
                        Please contact reception for fee information.
                      </Typography>
                    </Alert>
                  </Box>
                )}

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    startIcon={<VisibilityIcon />}
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setSelectedAppointment(appointment);
                      setShowDetailsModal(true);
                    }}
                    sx={{
                      borderColor: '#3B82F6',
                      color: '#3B82F6',
                      flex: 1,
                      minWidth: 100,
                      '&:hover': {
                        borderColor: '#2563EB',
                        backgroundColor: 'rgba(59, 130, 246, 0.04)'
                      }
                    }}
                  >
                    View
                  </Button>

                  <Button
                    startIcon={<EditIcon />}
                    variant="outlined"
                    size="small"
                    onClick={() => handleAppointmentAction('edit', appointment)}
                    sx={{
                      borderColor: '#8B5CF6',
                      color: '#8B5CF6',
                      flex: 1,
                      minWidth: 100,
                      '&:hover': {
                        borderColor: '#7C3AED',
                        backgroundColor: 'rgba(139, 92, 246, 0.04)'
                      }
                    }}
                  >
                    Edit
                  </Button>

                  <Button
                    startIcon={<LocalHospitalIcon />}
                    variant="outlined"
                    size="small"
                    onClick={() => handleAppointmentAction('admit', appointment)}
                    sx={{
                      borderColor: '#10B981',
                      color: '#10B981',
                      flex: 1,
                      minWidth: 100,
                      '&:hover': {
                        borderColor: '#059669',
                        backgroundColor: 'rgba(16, 185, 129, 0.04)'
                      }
                    }}
                  >
                    Admit
                  </Button>

                  <Button
                    startIcon={<PhoneIcon />}
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      if (appointment.phone && appointment.phone !== 'N/A') {
                        window.open(`tel:${appointment.phone}`, '_self');
                      }
                    }}
                    sx={{
                      borderColor: '#059669',
                      color: '#059669',
                      flex: 1,
                      minWidth: 100,
                      '&:hover': {
                        borderColor: '#047857',
                        backgroundColor: 'rgba(5, 150, 105, 0.04)'
                      }
                    }}
                  >
                    Call
                  </Button>
                </Box>
              </Box>
            </Card>
            );
          })
        )}
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
          setActionMenuAppointment(null);
        }}
      >
        <MenuItem onClick={() => handleAppointmentAction('view', actionMenuAppointment)}>
          View Details
        </MenuItem>
        {actionMenuAppointment?.status === 'Pending' && (
          <MenuItem onClick={() => handleAppointmentAction('confirm', actionMenuAppointment)}>
            Confirm
          </MenuItem>
        )}
        {(actionMenuAppointment?.status === 'Confirmed' || actionMenuAppointment?.status === 'Pending') && (
          <MenuItem onClick={() => handleAppointmentAction('complete', actionMenuAppointment)}>
            Mark Complete
          </MenuItem>
        )}
        {actionMenuAppointment?.status !== 'Cancelled' && actionMenuAppointment?.status !== 'Completed' && (
          <MenuItem onClick={() => handleAppointmentAction('cancel', actionMenuAppointment)}>
            Cancel
          </MenuItem>
        )}
      </Menu>

      {/* Appointment Details Modal */}
      <Dialog open={showDetailsModal} onClose={() => setShowDetailsModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
          <Box>
            <Box sx={{ fontSize: '1.25rem', fontWeight: 600 }}>Appointment Details</Box>
            {selectedAppointment?.appointmentNumber && (
              <Box sx={{ fontSize: '0.75rem', color: '#6B7280', mt: 0.5 }}>
                {selectedAppointment.appointmentNumber}
              </Box>
            )}
          </Box>
          <IconButton onClick={() => setShowDetailsModal(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedAppointment && (
            <Box>
              {/* Status Badge */}
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  icon={getStatusIcon(selectedAppointment.status)}
                  label={selectedAppointment.status}
                  sx={{
                    backgroundColor: getStatusColor(selectedAppointment.status),
                    color: '#FFFFFF',
                    fontWeight: 600
                  }}
                />
                {selectedAppointment.isNewPatient && (
                  <Chip label="New Patient" size="small" color="info" />
                )}
              </Box>

              {/* Patient Information */}
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#F9FAFB', borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon sx={{ fontSize: 20 }} /> Patient Information
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedAppointment.patientName}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>Patient ID</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedAppointment.appointmentNumber || selectedAppointment.tokenNumber || 'N/A'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>Age</Typography>
                    <Typography variant="body1">{selectedAppointment.age || selectedAppointment.patientAge || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>Gender</Typography>
                    <Typography variant="body1">{selectedAppointment.gender || selectedAppointment.patientGender || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>Phone</Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon sx={{ fontSize: 16 }} /> {selectedAppointment.phone}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>Emergency Contact</Typography>
                    <Typography variant="body1">{selectedAppointment.emergencyContact || selectedAppointment.phone || 'N/A'}</Typography>
                  </Box>
                  {selectedAppointment.patientAddress && (
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="caption" sx={{ color: '#6B7280' }}>Address</Typography>
                      <Typography variant="body2">{selectedAppointment.patientAddress}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Appointment Information */}
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#F9FAFB', borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EventIcon sx={{ fontSize: 20 }} /> Appointment Information
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>Doctor</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedAppointment.doctorName}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>Department</Typography>
                    <Typography variant="body1">{selectedAppointment.department}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>Service</Typography>
                    <Typography variant="body1">{selectedAppointment.service}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>Type</Typography>
                    <Typography variant="body1">{selectedAppointment.appointmentType}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>Date</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedAppointment.date}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>Time</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedAppointment.time}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Payment Information */}
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#F9FAFB', borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PaymentIcon sx={{ fontSize: 20 }} /> Payment Information
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>Fee Amount</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#059669' }}>₹{selectedAppointment.feeAmount}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>Payment Mode</Typography>
                    <Typography variant="body1">{selectedAppointment.paymentMode}</Typography>
                  </Box>
                  {selectedAppointment.paymentStatus && (
                    <Box>
                      <Typography variant="caption" sx={{ color: '#6B7280' }}>Payment Status</Typography>
                      <Chip 
                        label={selectedAppointment.paymentStatus} 
                        size="small"
                        color={selectedAppointment.paymentStatus === 'Paid' ? 'success' : 'warning'}
                      />
                    </Box>
                  )}
                  {selectedAppointment.transactionId && (
                    <Box>
                      <Typography variant="caption" sx={{ color: '#6B7280' }}>Transaction ID</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{selectedAppointment.transactionId}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Symptoms/Reason */}
              {selectedAppointment.symptoms && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#6B7280', mb: 1, fontWeight: 600 }}>Symptoms/Reason for Visit</Typography>
                  <Typography variant="body2" sx={{ p: 2, backgroundColor: '#F9FAFB', borderRadius: 1, border: '1px solid #E5E7EB' }}>
                    {selectedAppointment.symptoms}
                  </Typography>
                </Box>
              )}

              {/* Booking Date */}
              {selectedAppointment.bookingDate && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #E5E7EB' }}>
                  <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                    Booked on: {selectedAppointment.bookingDate}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={() => setShowDetailsModal(false)} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add New Appointment Modal */}
      <Dialog 
        open={showAddModal} 
        onClose={() => {
          setShowAddModal(false);
          resetForm();
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 3,
          pb: 1,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between'
        }}>
          <Box>
            <Box sx={{ 
              fontWeight: 'bold', 
              color: '#1F2937',
              fontSize: '1.5rem',
              mb: 0.5
            }}>
              Add New Appointment
            </Box>
            <Box sx={{ 
              color: '#6B7280',
              fontSize: '0.875rem'
            }}>
              Schedule a new patient appointment
            </Box>
          </Box>
          <IconButton 
            onClick={() => {
              setShowAddModal(false);
              resetForm();
            }}
            sx={{ 
              color: '#6B7280',
              '&:hover': { backgroundColor: '#F3F4F6' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ px: 3, py: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={3}>
              {/* Patient Information Section */}
              <Box>
                <Typography variant="h6" sx={{ 
                  fontSize: '1rem', 
                  fontWeight: '600', 
                  color: '#374151', 
                  mb: 2
                }}>
                  Patient Information
                </Typography>
                
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="body2" sx={{ 
                      color: '#374151', 
                      fontWeight: '500',
                      mb: 1,
                      fontSize: '0.875rem'
                    }}>
                      Patient Name <span style={{ color: '#EF4444' }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter patient name"
                      value={newAppointmentForm.patientName}
                      onChange={(e) => setNewAppointmentForm(prev => ({ ...prev, patientName: e.target.value }))}
                      error={!!formErrors.patientName}
                      helperText={formErrors.patientName}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: '#FAFAFA',
                          '& fieldset': {
                            borderColor: '#E5E7EB',
                          },
                          '&:hover fieldset': {
                            borderColor: '#D1D5DB',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#3B82F6',
                          },
                        }
                      }}
                    />
                  </Box>
                  
                  <Stack direction="row" spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ 
                        color: '#374151', 
                        fontWeight: '500',
                        mb: 1,
                        fontSize: '0.875rem'
                      }}>
                        Phone Number <span style={{ color: '#EF4444' }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="10-digit mobile number"
                        value={newAppointmentForm.phone}
                        onChange={(e) => {
                          // Only allow numbers
                          const numericValue = e.target.value.replace(/[^0-9]/g, '');
                          if (numericValue.length <= 10) {
                            setNewAppointmentForm(prev => ({ ...prev, phone: numericValue }));
                          }
                        }}
                        error={!!formErrors.phone}
                        helperText={
                          formErrors.phone || 
                          (newAppointmentForm.phone && newAppointmentForm.phone.length < 10 
                            ? `${10 - newAppointmentForm.phone.length} digits remaining` 
                            : newAppointmentForm.phone && newAppointmentForm.phone.length === 10 
                              ? '✓ Valid phone number' 
                              : '')
                        }
                        inputProps={{ 
                          maxLength: 10,
                          inputMode: 'numeric' as any,
                          pattern: '[0-9]*'
                        }}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: '#FAFAFA',
                            '& fieldset': {
                              borderColor: '#E5E7EB',
                            }
                          },
                          '& .MuiFormHelperText-root': {
                            color: newAppointmentForm.phone && newAppointmentForm.phone.length === 10 && !formErrors.phone ? '#10B981' : 
                                   newAppointmentForm.phone && newAppointmentForm.phone.length < 10 && !formErrors.phone ? '#F59E0B' : undefined
                          }
                        }}
                      />
                    </Box>
                    
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ 
                        color: '#374151', 
                        fontWeight: '500',
                        mb: 1,
                        fontSize: '0.875rem'
                      }}>
                        Age <span style={{ color: '#EF4444' }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Age"
                        type="number"
                        value={newAppointmentForm.age}
                        onChange={(e) => setNewAppointmentForm(prev => ({ ...prev, age: e.target.value }))}
                        error={!!formErrors.age}
                        helperText={formErrors.age}
                        inputProps={{ min: 1, max: 120 }}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: '#FAFAFA',
                            '& fieldset': {
                              borderColor: '#E5E7EB',
                            }
                          }
                        }}
                      />
                    </Box>
                  </Stack>
                  
                  <Box>
                    <Typography variant="body2" sx={{ 
                      color: '#374151', 
                      fontWeight: '500',
                      mb: 1,
                      fontSize: '0.875rem'
                    }}>
                      Gender <span style={{ color: '#EF4444' }}>*</span>
                    </Typography>
                    <FormControl fullWidth error={!!formErrors.gender}>
                      <Select
                        value={newAppointmentForm.gender}
                        onChange={(e) => setNewAppointmentForm(prev => ({ ...prev, gender: e.target.value }))}
                        displayEmpty
                        variant="outlined"
                        sx={{
                          borderRadius: '8px',
                          backgroundColor: '#FAFAFA',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E5E7EB',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#D1D5DB',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#3B82F6',
                          }
                        }}
                      >
                        <MenuItem value="" disabled>
                          <span style={{ color: '#9CA3AF' }}>Select gender</span>
                        </MenuItem>
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                      {formErrors.gender && <FormHelperText>{formErrors.gender}</FormHelperText>}
                    </FormControl>
                  </Box>
                </Stack>
              </Box>

              {/* Appointment Details Section */}
              <Box>
                <Typography variant="h6" sx={{ 
                  fontSize: '1rem', 
                  fontWeight: '600', 
                  color: '#374151', 
                  mb: 2
                }}>
                  Appointment Details
                </Typography>
                
                <Stack spacing={2.5}>
                  <Stack direction="row" spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ 
                        color: '#374151', 
                        fontWeight: '500',
                        mb: 1,
                        fontSize: '0.875rem'
                      }}>
                        Doctor <span style={{ color: '#EF4444' }}>*</span>
                      </Typography>
                      <FormControl fullWidth error={!!formErrors.doctorId}>
                        <Select
                          value={newAppointmentForm.doctorId}
                          onChange={(e) => {
                            const selectedDoctor = doctors.find(d => d.id === e.target.value);
                            setNewAppointmentForm(prev => ({ 
                              ...prev, 
                              doctorId: e.target.value,
                              department: selectedDoctor?.department || '',
                              service: ''
                            }));
                          }}
                          displayEmpty
                          variant="outlined"
                          sx={{
                            borderRadius: '8px',
                            backgroundColor: '#FAFAFA',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#E5E7EB',
                            }
                          }}
                        >
                          <MenuItem value="" disabled>
                            <span style={{ color: '#9CA3AF' }}>Select doctor</span>
                          </MenuItem>
                          {doctors.map((doctor: any) => (
                            <MenuItem key={doctor.id} value={doctor.id}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#1F2937' }}>
                                  {doctor.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                  {doctor.specialization}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                        {formErrors.doctorId && <FormHelperText>{formErrors.doctorId}</FormHelperText>}
                      </FormControl>
                    </Box>
                    
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ 
                        color: '#374151', 
                        fontWeight: '500',
                        mb: 1,
                        fontSize: '0.875rem'
                      }}>
                        Department <span style={{ color: '#EF4444' }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Auto-filled from doctor"
                        value={newAppointmentForm.department}
                        InputProps={{
                          readOnly: true,
                        }}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: '#F9FAFB',
                            color: newAppointmentForm.department ? '#374151' : '#9CA3AF',
                            '& fieldset': {
                              borderColor: '#E5E7EB',
                            }
                          }
                        }}
                      />
                    </Box>
                  </Stack>

                  <Box>
                    <Typography variant="body2" sx={{ 
                      color: '#374151', 
                      fontWeight: '500',
                      mb: 1,
                      fontSize: '0.875rem'
                    }}>
                      Service <span style={{ color: '#EF4444' }}>*</span>
                      {!newAppointmentForm.doctorId && (
                        <span style={{ color: '#F59E0B', fontSize: '0.75rem', marginLeft: '8px' }}>
                          (Select doctor first)
                        </span>
                      )}
                    </Typography>
                    <FormControl fullWidth error={!!formErrors.service}>
                      <Select
                        value={newAppointmentForm.service}
                        onChange={async (e) => {
                          const selectedService = e.target.value;
                          setNewAppointmentForm(prev => ({ ...prev, service: selectedService }));
                          
                          // Auto-populate fee for test services
                          if (selectedService && selectedService !== 'Others') {
                            try {
                              // Check if it's a test service by fetching from tests collection
                              const testsResponse = await TestsManagementService.getTests();
                              const matchingTest = testsResponse.find(test => 
                                test.name.toLowerCase() === selectedService.toLowerCase() ||
                                selectedService.toLowerCase().includes(test.name.toLowerCase())
                              );
                              
                              if (matchingTest && matchingTest.price) {
                                console.log(`💰 Auto-populated test price for ${selectedService}: ₹${matchingTest.price}`);
                                setNewAppointmentForm(prev => ({ 
                                  ...prev, 
                                  feeAmount: matchingTest.price.toString() 
                                }));
                              }
                            } catch (error) {
                              console.error('Error fetching test price:', error);
                              // Don't show error to user, just continue without auto-population
                            }
                          }
                        }}
                        disabled={!newAppointmentForm.doctorId}
                        displayEmpty
                        variant="outlined"
                        sx={{
                          borderRadius: '8px',
                          backgroundColor: newAppointmentForm.doctorId ? '#FAFAFA' : '#F9FAFB',
                          cursor: newAppointmentForm.doctorId ? 'pointer' : 'not-allowed',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: newAppointmentForm.doctorId ? '#E5E7EB' : '#E5E7EB',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: newAppointmentForm.doctorId ? '#D1D5DB' : '#E5E7EB',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#3B82F6',
                          },
                          '& .MuiSelect-select': {
                            color: newAppointmentForm.doctorId ? '#1F2937' : '#9CA3AF',
                          }
                        }}
                      >
                        <MenuItem value="" disabled>
                          <span style={{ color: '#9CA3AF' }}>
                            {newAppointmentForm.doctorId ? 'Select service' : 'Please select a doctor first'}
                          </span>
                        </MenuItem>
                        {getAvailableServices().map((service: string) => (
                          <MenuItem key={service} value={service}>
                            {service}
                          </MenuItem>
                        ))}
                        <MenuItem value="Others">Others (Specify below)</MenuItem>
                      </Select>
                      {formErrors.service && <FormHelperText>{formErrors.service}</FormHelperText>}
                      {!newAppointmentForm.doctorId && !formErrors.service && (
                        <FormHelperText sx={{ color: '#F59E0B' }}>
                          Select a doctor to see available services
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Box>

                  {newAppointmentForm.service === 'Others' && (
                    <Box>
                      <Typography variant="body2" sx={{ 
                        color: '#374151', 
                        fontWeight: '500',
                        mb: 1,
                        fontSize: '0.875rem'
                      }}>
                        Custom Service Name <span style={{ color: '#EF4444' }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter custom service name"
                        value={newAppointmentForm.customService || ''}
                        onChange={(e) => setNewAppointmentForm(prev => ({ ...prev, customService: e.target.value }))}
                        error={!!formErrors.customService}
                        helperText={formErrors.customService}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: '#FAFAFA',
                            '& fieldset': {
                              borderColor: '#E5E7EB',
                            }
                          }
                        }}
                      />
                    </Box>
                  )}

                  <Box>
                    <Typography variant="body2" sx={{ 
                      color: '#374151', 
                      fontWeight: '500',
                      mb: 1,
                      fontSize: '0.875rem'
                    }}>
                      Appointment Type <span style={{ color: '#EF4444' }}>*</span>
                    </Typography>
                    <FormControl fullWidth error={!!formErrors.appointmentType}>
                      <Select
                        value={newAppointmentForm.appointmentType}
                        onChange={(e) => setNewAppointmentForm(prev => ({ ...prev, appointmentType: e.target.value }))}
                        displayEmpty
                        variant="outlined"
                        sx={{
                          borderRadius: '8px',
                          backgroundColor: '#FAFAFA',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E5E7EB',
                          }
                        }}
                      >
                        <MenuItem value="" disabled>
                          <span style={{ color: '#9CA3AF' }}>Select appointment type</span>
                        </MenuItem>
                        <MenuItem value="consultation">Consultation</MenuItem>
                        <MenuItem value="follow-up">Follow-up</MenuItem>
                        <MenuItem value="emergency">Emergency</MenuItem>
                        <MenuItem value="routine-checkup">Routine Checkup</MenuItem>
                      </Select>
                      {formErrors.appointmentType && <FormHelperText>{formErrors.appointmentType}</FormHelperText>}
                    </FormControl>
                  </Box>

                  <Stack direction="row" spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ 
                        color: '#374151', 
                        fontWeight: '500',
                        mb: 1,
                        fontSize: '0.875rem'
                      }}>
                        Date <span style={{ color: '#EF4444' }}>*</span>
                      </Typography>
                      <DatePicker
                        value={newAppointmentForm.date}
                        onChange={(newValue) => setNewAppointmentForm(prev => ({ ...prev, date: newValue }))}
                        minDate={new Date()}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!formErrors.date,
                            helperText: formErrors.date,
                            placeholder: "Select date",
                            variant: "outlined",
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                backgroundColor: '#FAFAFA',
                                '& fieldset': {
                                  borderColor: '#E5E7EB',
                                }
                              }
                            }
                          }
                        }}
                      />
                    </Box>
                    
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ 
                        color: '#374151', 
                        fontWeight: '500',
                        mb: 1,
                        fontSize: '0.875rem'
                      }}>
                        Time <span style={{ color: '#EF4444' }}>*</span>
                      </Typography>
                      <TimePicker
                        value={newAppointmentForm.time}
                        onChange={(newValue) => setNewAppointmentForm(prev => ({ ...prev, time: newValue }))}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!formErrors.time,
                            helperText: formErrors.time,
                            placeholder: "Select time",
                            variant: "outlined",
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                backgroundColor: '#FAFAFA',
                                '& fieldset': {
                                  borderColor: '#E5E7EB',
                                }
                              }
                            }
                          }
                        }}
                      />
                    </Box>
                  </Stack>
                </Stack>
              </Box>

              {/* Payment & Additional Info Section */}
              <Box>
                <Typography variant="h6" sx={{ 
                  fontSize: '1rem', 
                  fontWeight: '600', 
                  color: '#374151', 
                  mb: 2
                }}>
                  Payment & Additional Info
                </Typography>
                
                <Stack spacing={2.5}>
                  <Stack direction="row" spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ 
                        color: '#374151', 
                        fontWeight: '500',
                        mb: 1,
                        fontSize: '0.875rem'
                      }}>
                        Payment Mode <span style={{ color: '#EF4444' }}>*</span>
                      </Typography>
                      <FormControl fullWidth error={!!formErrors.paymentMode}>
                        <Select
                          value={newAppointmentForm.paymentMode}
                          onChange={(e) => setNewAppointmentForm(prev => ({ ...prev, paymentMode: e.target.value }))}
                          displayEmpty
                          variant="outlined"
                          sx={{
                            borderRadius: '8px',
                            backgroundColor: '#FAFAFA',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#E5E7EB',
                            }
                          }}
                        >
                          <MenuItem value="" disabled>
                            <span style={{ color: '#9CA3AF' }}>Pay at Hospital</span>
                          </MenuItem>
                          {paymentModes.map((mode) => (
                            <MenuItem key={mode} value={mode}>
                              {mode}
                            </MenuItem>
                          ))}
                        </Select>
                        {formErrors.paymentMode && <FormHelperText>{formErrors.paymentMode}</FormHelperText>}
                      </FormControl>
                    </Box>
                    
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ 
                        color: '#374151', 
                        fontWeight: '500',
                        mb: 1,
                        fontSize: '0.875rem'
                      }}>
                        Fee Amount <span style={{ color: '#EF4444' }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="₹ 0"
                        type="number"
                        value={newAppointmentForm.feeAmount}
                        onChange={(e) => setNewAppointmentForm(prev => ({ ...prev, feeAmount: e.target.value }))}
                        error={!!formErrors.feeAmount}
                        helperText={formErrors.feeAmount}
                        inputProps={{ min: 0 }}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: '#FAFAFA',
                            '& fieldset': {
                              borderColor: '#E5E7EB',
                            }
                          }
                        }}
                      />
                    </Box>
                  </Stack>
                  
                  <Box>
                    <Typography variant="body2" sx={{ 
                      color: '#374151', 
                      fontWeight: '500',
                      mb: 1,
                      fontSize: '0.875rem'
                    }}>
                      Symptoms/Reason for Visit
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Describe symptoms or reason for appointment"
                      multiline
                      rows={3}
                      value={newAppointmentForm.symptoms}
                      onChange={(e) => setNewAppointmentForm(prev => ({ ...prev, symptoms: e.target.value }))}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: '#FAFAFA',
                          '& fieldset': {
                            borderColor: '#E5E7EB',
                          }
                        }
                      }}
                    />
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </LocalizationProvider>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            variant="contained" 
            onClick={handleCreateAppointment}
            disabled={submittingForm}
            fullWidth
            startIcon={newAppointmentForm.paymentMode === 'Razorpay' ? <></> : <AddIcon />}
            sx={{ 
              backgroundColor: newAppointmentForm.paymentMode === 'Razorpay' ? '#528FF0' : '#3B82F6',
              color: 'white',
              fontWeight: '600',
              fontSize: '1rem',
              py: 1.5,
              borderRadius: '8px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: newAppointmentForm.paymentMode === 'Razorpay' ? '#3B72D9' : '#2563EB'
              },
              '&.Mui-disabled': {
                backgroundColor: '#9CA3AF',
                color: 'white'
              }
            }}
          >
            {submittingForm 
              ? (newAppointmentForm.paymentMode === 'Razorpay' ? 'Processing Payment...' : 'Creating Appointment...') 
              : (newAppointmentForm.paymentMode === 'Razorpay' ? '💳 Pay with Razorpay' : 'Create Appointment')
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Appointment Modal */}
      <Dialog 
        open={showEditModal} 
        onClose={() => {
          setShowEditModal(false);
          setSelectedAppointment(null);
          resetForm();
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 3,
          pb: 1,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between'
        }}>
          <Box>
            <Box sx={{ 
              fontWeight: 'bold', 
              color: '#1F2937',
              fontSize: '1.5rem',
              mb: 0.5
            }}>
              Edit Appointment
            </Box>
            <Box sx={{ 
              color: '#6B7280',
              fontSize: '0.875rem'
            }}>
              Update appointment details for {selectedAppointment?.patientName}
            </Box>
          </Box>
          <IconButton 
            onClick={() => {
              setShowEditModal(false);
              setSelectedAppointment(null);
              resetForm();
            }}
            sx={{ 
              color: '#6B7280',
              '&:hover': { backgroundColor: '#F3F4F6' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
            Make changes to the appointment details below
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={2.5}>
              {/* Patient Information Section */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#374151' }}>
                  Patient Information
                </Typography>
                
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Patient Name *"
                    value={newAppointmentForm.patientName}
                    onChange={(e) => setNewAppointmentForm(prev => ({ ...prev, patientName: e.target.value }))}
                    error={!!formErrors.patientName}
                    helperText={formErrors.patientName}
                  />
                  
                  <Stack direction="row" spacing={2}>
                    <TextField
                      fullWidth
                      label="Phone Number *"
                      value={newAppointmentForm.phone}
                      onChange={(e) => {
                        const numericValue = e.target.value.replace(/[^0-9]/g, '');
                        if (numericValue.length <= 10) {
                          setNewAppointmentForm(prev => ({ ...prev, phone: numericValue }));
                        }
                      }}
                      error={!!formErrors.phone}
                      helperText={formErrors.phone}
                      placeholder="10-digit number"
                    />
                    
                    <TextField
                      fullWidth
                      label="Age *"
                      type="number"
                      value={newAppointmentForm.age}
                      onChange={(e) => setNewAppointmentForm(prev => ({ ...prev, age: e.target.value }))}
                      error={!!formErrors.age}
                      helperText={formErrors.age}
                    />
                  </Stack>
                  
                  <FormControl fullWidth error={!!formErrors.gender}>
                    <InputLabel>Gender *</InputLabel>
                    <Select
                      value={newAppointmentForm.gender}
                      label="Gender *"
                      onChange={(e) => setNewAppointmentForm(prev => ({ ...prev, gender: e.target.value }))}
                    >
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                    {formErrors.gender && <FormHelperText>{formErrors.gender}</FormHelperText>}
                  </FormControl>
                </Stack>
              </Box>

              {/* Appointment Details Section */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#374151' }}>
                  Appointment Details
                </Typography>
                
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2}>
                    <FormControl fullWidth error={!!formErrors.doctorId}>
                      <InputLabel>Doctor *</InputLabel>
                      <Select
                        value={newAppointmentForm.doctorId}
                        label="Doctor *"
                        onChange={(e) => {
                          const doctor = doctors.find(d => d.id === e.target.value);
                          setNewAppointmentForm(prev => ({ 
                            ...prev, 
                            doctorId: e.target.value,
                            department: doctor?.department || prev.department
                          }));
                        }}
                      >
                        {doctors.map((doctor) => (
                          <MenuItem key={doctor.id} value={doctor.id}>
                            {doctor.name} - {doctor.department}
                          </MenuItem>
                        ))}
                      </Select>
                      {formErrors.doctorId && <FormHelperText>{formErrors.doctorId}</FormHelperText>}
                    </FormControl>

                    <FormControl fullWidth error={!!formErrors.service}>
                      <InputLabel>Service *</InputLabel>
                      <Select
                        value={newAppointmentForm.service}
                        label="Service *"
                        onChange={(e) => setNewAppointmentForm(prev => ({ ...prev, service: e.target.value }))}
                      >
                        {getAvailableServices().map((service) => (
                          <MenuItem key={service} value={service}>
                            {service}
                          </MenuItem>
                        ))}
                      </Select>
                      {formErrors.service && <FormHelperText>{formErrors.service}</FormHelperText>}
                    </FormControl>
                  </Stack>
                  
                  <Stack direction="row" spacing={2}>
                    <DatePicker
                      label="Date *"
                      value={newAppointmentForm.date}
                      onChange={(newValue) => setNewAppointmentForm(prev => ({ ...prev, date: newValue }))}
                      minDate={new Date()}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!formErrors.date,
                          helperText: formErrors.date
                        }
                      }}
                    />
                    
                    <TimePicker
                      label="Time *"
                      value={newAppointmentForm.time}
                      onChange={(newValue) => setNewAppointmentForm(prev => ({ ...prev, time: newValue }))}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!formErrors.time,
                          helperText: formErrors.time
                        }
                      }}
                    />
                  </Stack>

                  <FormControl fullWidth error={!!formErrors.appointmentType}>
                    <InputLabel>Appointment Type *</InputLabel>
                    <Select
                      value={newAppointmentForm.appointmentType}
                      label="Appointment Type *"
                      onChange={(e) => setNewAppointmentForm(prev => ({ ...prev, appointmentType: e.target.value }))}
                    >
                      <MenuItem value="consultation">Consultation</MenuItem>
                      <MenuItem value="follow-up">Follow-up</MenuItem>
                      <MenuItem value="emergency">Emergency</MenuItem>
                      <MenuItem value="routine-checkup">Routine Checkup</MenuItem>
                    </Select>
                    {formErrors.appointmentType && <FormHelperText>{formErrors.appointmentType}</FormHelperText>}
                  </FormControl>
                  
                  <TextField
                    fullWidth
                    label="Symptoms/Reason"
                    multiline
                    rows={2}
                    value={newAppointmentForm.symptoms}
                    onChange={(e) => setNewAppointmentForm(prev => ({ ...prev, symptoms: e.target.value }))}
                    placeholder="Describe symptoms or reason for visit"
                  />
                </Stack>
              </Box>

              {/* Payment Section */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#374151' }}>
                  Payment Information
                </Typography>
                
                <Stack direction="row" spacing={2}>
                  <TextField
                    fullWidth
                    label="Fee Amount *"
                    type="number"
                    value={newAppointmentForm.feeAmount}
                    onChange={(e) => setNewAppointmentForm(prev => ({ ...prev, feeAmount: e.target.value }))}
                    error={!!formErrors.feeAmount}
                    helperText={formErrors.feeAmount}
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1, color: '#6B7280' }}>₹</Typography>
                    }}
                  />
                  
                  <FormControl fullWidth error={!!formErrors.paymentMode}>
                    <InputLabel>Payment Mode *</InputLabel>
                    <Select
                      value={newAppointmentForm.paymentMode}
                      label="Payment Mode *"
                      onChange={(e) => setNewAppointmentForm(prev => ({ ...prev, paymentMode: e.target.value }))}
                    >
                      {paymentModes.map((mode) => (
                        <MenuItem key={mode} value={mode}>{mode}</MenuItem>
                      ))}
                    </Select>
                    {formErrors.paymentMode && <FormHelperText>{formErrors.paymentMode}</FormHelperText>}
                  </FormControl>
                </Stack>
              </Box>
            </Stack>
          </LocalizationProvider>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={() => {
              setShowEditModal(false);
              setSelectedAppointment(null);
              resetForm();
            }}
            sx={{ color: '#6B7280' }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={handleEditAppointment}
            disabled={submittingForm}
            sx={{
              backgroundColor: '#8B5CF6',
              '&:hover': { backgroundColor: '#7C3AED' },
              '&.Mui-disabled': {
                backgroundColor: '#9CA3AF',
                color: 'white'
              }
            }}
          >
            {submittingForm ? 'Updating...' : 'Update Appointment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Menu - Three Dots */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={handleStatusMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            minWidth: 180
          }
        }}
      >
        <MenuItem onClick={() => handleStatusChange('Pending')}>
          <PendingIcon sx={{ mr: 1, color: '#F59E0B', fontSize: 20 }} />
          Pending
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('Confirm')}>
          <CheckCircleIcon sx={{ mr: 1, color: '#10B981', fontSize: 20 }} />
          Confirm
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('Completed')}>
          <CheckCircleIcon sx={{ mr: 1, color: '#059669', fontSize: 20 }} />
          Completed
        </MenuItem>
      </Menu>

      {/* Payment Collection Dialog */}
      <Dialog
        open={showPaymentDialog}
        onClose={() => !processingPayment && setShowPaymentDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#F9FAFB', 
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box>
            <Box sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
              Collect Payment
            </Box>
            <Box sx={{ color: '#6B7280', fontSize: '0.75rem', mt: 0.5 }}>
              {selectedPaymentAppointment?.patientName}
            </Box>
          </Box>
          <IconButton onClick={() => setShowPaymentDialog(false)} disabled={processingPayment}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>
              Payment Amount
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1, color: '#6B7280' }}>₹</Typography>
              }}
              disabled={processingPayment}
            />
          </Box>

          <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
            Select Payment Method:
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<CurrencyRupeeIcon />}
              onClick={handleCashPayment}
              disabled={processingPayment}
              sx={{
                justifyContent: 'flex-start',
                p: 2,
                borderColor: '#10B981',
                color: '#10B981',
                '&:hover': {
                  borderColor: '#059669',
                  backgroundColor: 'rgba(16, 185, 129, 0.04)'
                }
              }}
            >
              <Box sx={{ textAlign: 'left', flex: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Cash Payment
                </Typography>
                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                  Collect cash payment at the counter
                </Typography>
              </Box>
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<CreditCardIcon />}
              onClick={handleRazorpayPaymentForExistingAppointment}
              disabled={processingPayment}
              sx={{
                justifyContent: 'flex-start',
                p: 2,
                borderColor: '#3B82F6',
                color: '#3B82F6',
                '&:hover': {
                  borderColor: '#2563EB',
                  backgroundColor: 'rgba(59, 130, 246, 0.04)'
                }
              }}
            >
              <Box sx={{ textAlign: 'left', flex: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Razorpay Payment
                </Typography>
                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                  Card, UPI, Net Banking, Wallets
                </Typography>
              </Box>
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Cash Payment Confirmation Dialog */}
      <Dialog
        open={showCashConfirmDialog}
        onClose={() => !processingPayment && setShowCashConfirmDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#F9FAFB', 
          borderBottom: '1px solid #E5E7EB' 
        }}>
          <Box sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
            Confirm Cash Payment
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <CurrencyRupeeIcon sx={{ fontSize: 64, color: '#10B981', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1F2937', mb: 1 }}>
              ₹{paymentAmount}
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280' }}>
              Have you received this cash payment from
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#1F2937', mt: 1 }}>
              {selectedPaymentAppointment?.patientName}?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => {
              setShowCashConfirmDialog(false);
              setShowPaymentDialog(true);
            }}
            disabled={processingPayment}
            sx={{ color: '#6B7280' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmCashPayment}
            disabled={processingPayment}
            sx={{
              backgroundColor: '#10B981',
              '&:hover': { backgroundColor: '#059669' }
            }}
          >
            {processingPayment ? <CircularProgress size={24} /> : 'Confirm Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Change Confirmation Dialog */}
      <Dialog
        open={showStatusConfirmDialog}
        onClose={() => setShowStatusConfirmDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#F9FAFB', 
          borderBottom: '1px solid #E5E7EB' 
        }}>
          <Box sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
            Confirm Status Change
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          <Typography variant="body1" sx={{ color: '#4B5563' }}>
            Are you sure you want to change the appointment status to{' '}
            <Typography component="span" sx={{ fontWeight: 'bold', color: '#1F2937' }}>
              {pendingStatusChange}
            </Typography>
            ?
          </Typography>
          {pendingStatusChange === 'Completed' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This will mark the appointment as completed. Make sure payment is collected if required.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => {
              setShowStatusConfirmDialog(false);
              setPendingStatusChange('');
              setStatusMenuAppointment(null); // Clear appointment on cancel
            }}
            sx={{ color: '#6B7280' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmStatusChange}
            sx={{
              backgroundColor: '#3B82F6',
              '&:hover': { backgroundColor: '#2563EB' }
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Appointments;